import React from 'react';
import { withStyles } from 'tss-react/mui';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormLabel from '@mui/material/FormLabel';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import WaveTextField from '@common/components/form/WaveTextField';
import { DefaultCheckbox } from '@common/components/default-checkbox';
import MetadataService from '@common/services/MetadataService';

const styles = (theme) => ({
  container: {
    padding: theme.typography.pxToRem(25),
    paddingTop: theme.typography.pxToRem(22)
  },
  header: {
    paddingBottom: theme.typography.pxToRem(12)
  },
  registrationCutoff: {
    paddingBottom: theme.typography.pxToRem(15)
  },
  formLeft: {
    paddingRight: theme.typography.pxToRem(10)
  },
  formRight: {
    paddingLeft: theme.typography.pxToRem(10)
  },
  checkboxLabel: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.45,
    letterSpacing: '0.00938em',
    paddingBottom: theme.typography.pxToRem(8)
  },
  checkboxField: {
    marginRight: theme.typography.pxToRem(16)
  }
});

class ExternalCurrencyRequests extends React.Component {
  meta = new MetadataService();

  handleInputChange = (event) => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    let institute = { ...this.props.instituteDetails };
    institute[name] = value;

    this.props.onChange(institute);
  };

  handleCheckboxChange = (event) => {
    const target = event.target;
    const name = target.name;
    const value = target.value;
    const checked = target.checked;

    let institute = { ...this.props.instituteDetails };

    if (checked) {
      if (!institute[name].includes(value)) {
        institute[name] = [...institute[name], value];
      }
    } else {
      institute[name] = institute[name].filter((item) => item !== value);
    }

    this.props.onChange(institute);
  };

  errorHelpers = (field) => {
    if (this.props.validationErrors.hasOwnProperty(field)) {
      return this.props.validationErrors[field][0];
    }
    return null;
  };

  errorStatus = (field) => {
    return this.props.validationErrors.hasOwnProperty(field);
  };

  makeFieldProps = (field) => {
    return {
      error: this.errorStatus(field),
      helperText: this.errorHelpers(field),
      name: field,
      onChange: this.handleInputChange,
      value: this.props.instituteDetails[field]
    };
  };

  render() {
    const { classes, instituteDetails } = this.props;
    const instituteMeta = this.meta.get('institutes');
    const {
      vocational_point_approval_email_to: { options: vocationalPointEmailTo },
      professional_point_approval_email_to: {
        options: professionalPointEmailTo
      },
      allow_supervisor_to_action: { options: allowSupervisorToAction }
    } = instituteMeta;

    return (
      <form noValidate autoComplete="off">
        <Grid container spacing={2}>
          <Grid size={12}>
            <Paper className={classes.container}>
              <Typography className={classes.header} variant="h6">
                Dashboard
              </Typography>
              <Grid container spacing={4}>
                <Grid size={{xs:12, md:6}}>
                  <WaveTextField
                    label="Warning Days"
                    {...this.makeFieldProps('point_request_warning_days')}
                  />
                </Grid>
                <Grid size={{xs:12, md:6}}>
                  <WaveTextField
                    label="Overdue Days"
                    {...this.makeFieldProps('point_request_overdue_days')}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid size={12}>
            <Paper className={classes.container}>
              <Typography className={classes.header} variant="h6">
                Configuration
              </Typography>
              <Grid container>
                <Grid size={12}>
                  <DefaultCheckbox
                    label="Enable educator points entry"
                    checkboxValue={true}
                    unCheckValue={false}
                    {...this.makeFieldProps('educator_points_entry_enabled')}
                  />
                </Grid>
                <Grid className={classes.formLeft} size={6}>
                  <DefaultCheckbox
                    label="Enable points file attachment"
                    checkboxValue={true}
                    unCheckValue={false}
                    {...this.makeFieldProps('points_file_attachment_enabled')}
                  />
                </Grid>
                <Grid className={classes.formRight} size={6}>
                  <DefaultCheckbox
                    label="File attachment required"
                    checkboxValue={true}
                    unCheckValue={false}
                    {...this.makeFieldProps('file_attachment_required')}
                  />
                </Grid>
                <Grid className={classes.formLeft} size={6} sx={{ mb: 2, mt: 2 }}>
                  <FormLabel className={classes.checkboxLabel}>
                    Vocational points approval email to
                  </FormLabel>
                  <FormGroup row>
                    {Object.keys(vocationalPointEmailTo).map(
                      (emailTo, index) => (
                        <FormControlLabel
                          key={index}
                          className={classes.checkboxField}
                          control={
                            <Checkbox
                              checked={instituteDetails.vocational_point_approval_email_to.includes(
                                vocationalPointEmailTo[emailTo]
                              )}
                              name="vocational_point_approval_email_to"
                              value={vocationalPointEmailTo[emailTo]}
                              onChange={this.handleCheckboxChange}
                            />
                          }
                          label={emailTo}
                        />
                      )
                    )}
                  </FormGroup>
                  <Typography variant="caption" color="textSecondary">
                    The &ldquo;Supervisor&rdquo; option will send to both Supervisor and Supervisor2.<br />
                    The &ldquo;Admin&rdquo; option will send to both Admin and Institute Admin roles.
                  </Typography>
                </Grid>
                <Grid className={classes.formRight} size={6} sx={{ mb: 2, mt: 2 }}>
                  <FormLabel className={classes.checkboxLabel}>
                    Professional points approval email to
                  </FormLabel>
                  <FormGroup row>
                    {Object.keys(professionalPointEmailTo).map(
                      (emailTo, index) => (
                        <FormControlLabel
                          key={index}
                          className={classes.checkboxField}
                          control={
                            <Checkbox
                              checked={instituteDetails.professional_point_approval_email_to.includes(
                                professionalPointEmailTo[emailTo]
                              )}
                              name="professional_point_approval_email_to"
                              value={professionalPointEmailTo[emailTo]}
                              onChange={this.handleCheckboxChange}
                            />
                          }
                          label={emailTo}
                        />
                      )
                    )}
                  </FormGroup>
                  <Typography variant="caption" color="textSecondary">
                    The &ldquo;Supervisor&rdquo; option will send to both Supervisor and Supervisor2.<br />
                    The &ldquo;Admin&rdquo; option will send to both Admin and Institute Admin roles.
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <FormLabel className={classes.checkboxLabel}>
                    Allow Supervisor role to action
                  </FormLabel>
                  <FormGroup row>
                    {Object.keys(allowSupervisorToAction).map(
                      (action, index) => (
                        <FormControlLabel
                          key={index}
                          className={classes.checkboxField}
                          control={
                            <Checkbox
                              checked={instituteDetails.allow_supervisor_to_action.includes(
                                allowSupervisorToAction[action]
                              )}
                              name="allow_supervisor_to_action"
                              value={allowSupervisorToAction[action]}
                              onChange={this.handleCheckboxChange}
                            />
                          }
                          label={action}
                        />
                      )
                    )}
                  </FormGroup>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </form>
    );
  }
}

export default withStyles(ExternalCurrencyRequests, styles, {
  withTheme: true
});
